/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'
import { Card, CardContent, CardExtra } from '../../Shared/Card'
import { useQueries } from '@tanstack/react-query'
import { countMerchantItems } from '../../../../services/Item/Item.service'
import { TIME } from '../../../../services/date.service'
import { Icon, Statistic, StatisticValue, Progress } from 'semantic-ui-react'
import { Link } from 'react-router-dom-v5-compat'
import { LoadingPlaceholderCardContent } from '../../Shared/LoadingPlaceholderCardContent'
import { CardTitle } from '../../Shared/CardTitle'
import { useTranslation } from 'react-i18next'
import { useDeviceMediaQuery } from '../../../Shared/UseDeviceMediaQuery/useDeviceMediaQuery'

const sectionContainerStyle = css`
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
`

const progressBarStyle = css`
    width: 100%;

    &.ui.small.progress {
        margin: 0;
    }
`

const descriptionTextStyle = css`
    font-size: 0.85rem;
    text-align: center;
`

const getHealthColor = (score) => {
    if (score >= 90) return 'green'
    if (score >= 70) return 'olive'
    if (score >= 50) return 'yellow'
    if (score >= 30) return 'orange'
    return 'red'
}

const CatalogHealthCard = () => {
    const [t] = useTranslation()
    const { isMobile } = useDeviceMediaQuery()

    const queries = useQueries({
        queries: [{
            queryKey: ['catalog', 'total-items-count'],
            queryFn: async () => await countMerchantItems({}),
            staleTime: TIME.HOUR_IN_MS * 4,
        }, {
            queryKey: ['catalog', 'items-without-image-count'],
            queryFn: async () => await countMerchantItems({withImage: false}),
            staleTime: TIME.HOUR_IN_MS * 4,
        }, {
            queryKey: ['catalog', 'items-without-category-count'],
            queryFn: async () => await countMerchantItems({excludeCategories: true}),
            staleTime: TIME.HOUR_IN_MS * 4,
        }],
    })

    const isLoading = queries.some(query => query.isLoading)

    const totalItemsCount = queries[0].data || 0
    const itemsWithoutImageCount = queries[1].data || 0
    const itemsWithoutCategoryCount = queries[2].data || 0

    const itemsWithImageCount = totalItemsCount - itemsWithoutImageCount
    const itemsWithCategoryCount = totalItemsCount - itemsWithoutCategoryCount

    const imageCompletionRate = totalItemsCount > 0
      ? ((itemsWithImageCount / totalItemsCount) * 100).toFixed(1)
      : 100

    const categoryCompletionRate = totalItemsCount > 0
      ? ((itemsWithCategoryCount / totalItemsCount) * 100).toFixed(1)
      : 100

    const healthScore = totalItemsCount > 0
      ? ((parseFloat(imageCompletionRate) + parseFloat(categoryCompletionRate)) / 2).toFixed(1)
      : 100

    const getBorderColor = () => {
        if (isLoading) {
            return 'unset'
        }

        if (healthScore >= 90) return '#21ba45'
        if (healthScore >= 70) return '#b5cc18'
        if (healthScore >= 50) return '#fbbd08'
        if (healthScore >= 30) return '#f2711c'
        return '#db2828'
    }

    return (
      <Card
        css={css`
            border-top: 2px solid ${getBorderColor()};

            .card-content {
                width: ${isMobile ? '340px' : '360px'};
                height: 200px;
            }
        `}
      >
          {isLoading ? (
            <LoadingPlaceholderCardContent/>
          ) : (
            <CardContent
              css={css`
                  display: grid;
                  place-items: center;
                  gap: .75rem;
                  grid-template-areas:
                          'title title'
                          'health health'
                          'images categories';
                  grid-template-rows: 30px 90px 1fr;
                  grid-template-columns: 1fr 1fr;
              `}
            >
                <>
                    <CardTitle
                      title={t('dashboard.catalog_health.title')}
                    />

                    <div
                      css={css`
                          grid-area: health;
                          ${sectionContainerStyle}
                      `}
                    >
                        <Statistic
                          size="small"
                          color={getHealthColor(healthScore)}
                          css={css`
                              &.ui.statistic {
                                  margin: 0;
                              }
                          `}
                        >
                            <StatisticValue>
                                {healthScore}%
                            </StatisticValue>
                        </Statistic>
                        <div
                          css={css`
                              font-size: 0.85rem;
                              color: rgba(0, 0, 0, 0.6);
                          `}
                        >
                            {t('dashboard.catalog_health.x_total_products', {count: totalItemsCount})}
                        </div>
                    </div>

                    <div
                      css={css`
                          grid-area: images;
                          align-self: start;
                          padding-left: 1rem;
                          ${sectionContainerStyle}
                      `}
                    >
                        <Progress
                          percent={imageCompletionRate}
                          size="small"
                          color={getHealthColor(imageCompletionRate)}
                          progress
                          css={progressBarStyle}
                        />
                        <Link
                          to={{
                              pathname: '/products',
                              search: new URLSearchParams({
                                  withImage: 'false',
                              }).toString(),
                          }}
                          css={descriptionTextStyle}
                        >
                            {itemsWithoutImageCount > 0
                              ? t('dashboard.catalog_health.x_products_missing_image', {count: itemsWithoutImageCount})
                              : t('dashboard.catalog_health.all_products_have_images')}
                        </Link>
                    </div>

                    <div
                      css={css`
                          grid-area: categories;
                          align-self: start;
                          padding-right: 1rem;
                          ${sectionContainerStyle}
                      `}
                    >
                        <Progress
                          percent={categoryCompletionRate}
                          size="small"
                          color={getHealthColor(categoryCompletionRate)}
                          progress
                          css={progressBarStyle}
                        />
                        <Link
                          to={{
                              pathname: '/products',
                              search: new URLSearchParams({
                                  excludeCategories: 'true',
                              }).toString(),
                          }}
                          css={descriptionTextStyle}
                        >
                            {itemsWithoutCategoryCount > 0
                              ? t('dashboard.catalog_health.x_products_without_category', {count: itemsWithoutCategoryCount})
                              : t('dashboard.catalog_health.all_products_are_categorized')}
                        </Link>
                    </div>
                </>
            </CardContent>
          )}
          <CardExtra>
              <Link
                to={{
                    pathname: '/products',
                    search: new URLSearchParams({
                        unavailableUntil: 'false',
                    }).toString(),
                }}>
                  {t('dashboard.catalog_health.extra_link')}
                  <Icon name="arrow right"/>
              </Link>
          </CardExtra>
      </Card>
    )
}

export { CatalogHealthCard }

